import React, { useEffect, useRef, useState } from 'react';
import usePrivate from '@afosto/auth-react/usePrivate';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import FulfilmentLocationDetails from './FulfilmentLocationDetails';
import FulfilmentLocationProvider from '../../components/FulfilmentLocationProvider';
import FulfilmentLocationPositions from './FulfimentLocationPositions';
import FulfilmentLocationSorting from './FulfilmentLocationSorting';
import Page from '@afosto/app-components-react/Page';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import translations from './translations';

const ViewFulfilmentLocation = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:locations:read',
    'rel:contacts:read',
  ]);

  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const container = useRef(null);
  const { fulfilmentLocationID } = useParams();
  const [fulfilmentLocation, setFulfilmentLocation] = useState(null);
  const [isFetchingFulfilmentLocation, setIsFetchingFulfilmentLocation] = useState(true);
  const [fulfilmentPositions, setFulfilmentPositions] = useState([]);
  const [showUpdateSKUPosition, setShowUpdateSKUPosition] = useState(false);
  const [showAddMultiplePositions, setShowAddMultiplePositions] = useState(false);
  const [shouldLoadLocation, setShouldLoadLocation] = useState(true);
  const { is_disabled: isDeleted } = fulfilmentLocation || {};

  const handleDeleteFulfilmentLocation = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDelete.title),
        description: intl.formatMessage(translations.confirmDelete.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDelete.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDelete.buttons.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'delete') {
        const response = await fulfilmentApi.updateFulfilmentLocation(fulfilmentLocationID, {
          name: fulfilmentLocation.name,
          address_id: fulfilmentLocation.address_id,
          is_disabled: true,
        });
        const { data = {} } = response;
        setFulfilmentLocation(data);

        enqueueSnackbar(intl.formatMessage(translations.feedback.fulfilmentLocationDeleted), {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.fulfilmentLocationNotDeleted), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchFulfilmentLocation = async () => {
      setIsFetchingFulfilmentLocation(true);

      try {
        const response = await fulfilmentApi.getFulfilmentLocation(fulfilmentLocationID);
        const { data } = response;

        if (data?.address_id) {
          const addressResponse = await addressesApi.viewAddress(data.address_id).catch(() => {});
          const { data: addressData } = addressResponse;
          data.address = addressData
            ? {
                ...addressData,
                label: `${addressData.address_line_1} ${addressData.postal_code} ${addressData.locality} ${addressData.country_code}`,
                title: `${addressData.address_line_1} ${addressData.postal_code} ${addressData.locality} ${addressData.country_code}`,
                description: `${addressData.family_name} ${addressData.organisation}`,
                value: addressData.id,
              }
            : null;
        }

        const positionsResponse = await fulfilmentApi
          .listPositions(fulfilmentLocationID, {
            headers: {
              'x-page-size': 250,
            },
          })
          .catch(() => {});
        const { data: positionData } = positionsResponse;

        if (isMounted) {
          setFulfilmentLocation(data);
          setFulfilmentPositions(positionData ?? []);
        }
      } catch (err) {
        if (isMounted) {
          setFulfilmentLocation(null);
          setFulfilmentPositions([]);
        }
      } finally {
        setShouldLoadLocation(false);
        if (isMounted) {
          setIsFetchingFulfilmentLocation(false);
        }
      }
    };

    if (fulfilmentLocationID || shouldLoadLocation) {
      fetchFulfilmentLocation();
    }

    return () => {
      isMounted = false;
    };
  }, [fulfilmentLocationID, shouldLoadLocation]);

  if (!hasPermissions()) {
    return (
      <Page title={fulfilmentLocation && fulfilmentLocation.name} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingFulfilmentLocation) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8} xl={9}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4} xl={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SkeletonPage>
    );
  }

  return (
    <FulfilmentLocationProvider
      value={{
        fulfilmentLocation,
        fulfilmentPositions,
        setFulfilmentPositions,
        setShouldLoadLocation,
        showUpdateSKUPosition,
        setShowUpdateSKUPosition,
        showAddMultiplePositions,
        setShowAddMultiplePositions,
      }}
    >
      <Grid container wrap="nowrap">
        <Page
          title={
            (fulfilmentLocation && fulfilmentLocation.name) ||
            intl.formatMessage(translations.unknownLocation.title)
          }
          fullWidth
          subNavigation={{
            current: location.pathname,
            tabs: [
              {
                label: intl.formatMessage(translations.subNavigation.details),
                path: `/settings/fulfilment-locations/${fulfilmentLocationID}`,
              },
              {
                label: intl.formatMessage(translations.subNavigation.positions),
                path: `/settings/fulfilment-locations/${fulfilmentLocationID}/positions`,
              },
              {
                label: intl.formatMessage(translations.subNavigation.sorting),
                path: `/settings/fulfilment-locations/${fulfilmentLocationID}/sorting`,
              },
            ],
            onChange: (e, value) => history.replace(value),
          }}
          breadcrumbs={[
            {
              label: intl.formatMessage(translations.breadcrumb.settings),
              path: '/settings',
            },
            {
              label: intl.formatMessage(translations.breadcrumb.fulfilmentLocations),
              path: '/settings/fulfilment-locations',
            },
            {
              label: fulfilmentLocation && fulfilmentLocation.name,
              path: `/settings/fulfilment-locations/${fulfilmentLocationID}`,
              active: true,
            },
          ]}
          linkComponent={Link}
          primaryAction={
            location.pathname === `/settings/fulfilment-locations/${fulfilmentLocationID}/positions`
              ? {
                  text: intl.formatMessage(translations.primaryAction.add.label),
                  actions: [
                    ...(fulfilmentPositions?.length < 250
                      ? [
                          {
                            text: intl.formatMessage(translations.primaryAction.add.label),
                            action: () => setShowUpdateSKUPosition(true),
                            icon: ['fal', 'plus'],
                          },
                        ]
                      : []),
                    {
                      text: intl.formatMessage(translations.primaryAction.addMultiple.label),
                      action: () => setShowAddMultiplePositions(true),
                      icon: ['fal', 'plus'],
                    },
                  ],
                }
              : null
          }
          secondaryActions={[
            ...(hasPermissions(['lcs:locations:write'])
              ? [
                  {
                    text: intl.formatMessage(translations.actions.delete),
                    action: handleDeleteFulfilmentLocation,
                    color: 'secondary',
                    icon: ['fal', 'trash-alt'],
                  },
                ]
              : []),
          ]}
          isLoading={isFetchingFulfilmentLocation}
          style={{ flexGrow: 1 }}
        >
          {isDeleted && (
            <Box pb={2}>
              <Alert severity="error">
                <FormattedMessage {...translations.deletedMessage} />
              </Alert>
            </Box>
          )}
          {fulfilmentLocation ? (
            <Switch>
              <Route
                component={FulfilmentLocationSorting}
                path="/settings/fulfilment-locations/:fulfilmentID/sorting"
              />
              <Route
                render={props => <FulfilmentLocationPositions {...props} container={container} />}
                path="/settings/fulfilment-locations/:fulfilmentID/positions"
              />
              <Route
                component={FulfilmentLocationDetails}
                path="/settings/fulfilment-locations/:fulfilmentID"
              />
            </Switch>
          ) : (
            <Alert severity="error">
              {intl.formatMessage(translations.unknownLocation.message)}
            </Alert>
          )}
        </Page>
        <div ref={container} />
      </Grid>
    </FulfilmentLocationProvider>
  );
};

export default ViewFulfilmentLocation;
